import { Pause, Phone, PhoneOff, PhoneOutgoing, Repeat, Users, X } from "lucide-react";
import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Callout, EmptyState, Spinner } from "@/components/ui/feedback";
import { FrameTop, Page } from "@/components/layout/page";
import { AskCloserButton, AskCloserStrip, CloserHoldBanner } from "@/features/closer-ask";
import { knownCallType } from "@/features/call-type";
import { useAuth } from "@/lib/auth";
import { contractOffered } from "@/lib/call-contract";
import { isDialable } from "@/lib/phone-number";
import { colleagueFor, daySummary, legText, ownNumber } from "@/lib/phone-page";
import { readPeopleShown, textArrival, writePeopleShown, type TextsSeen } from "@/lib/phone-work";
import type { ContractAsk } from "@/lib/send-contract";
import { useCalls, useCallSummary, useColleagues } from "@/lib/queries";
import { inviteText, rosterShown } from "@/lib/rooms";
import { OWN_NON_CALL_LEGS } from "@/lib/types";
import { useMediaQuery } from "@/lib/use-media-query";
import { cn, formatDuration, formatPhoneNumber, relativeTime } from "@/lib/utils";
import {
  AcdCallChip, AcdCallStrip, AcdHandoffNote, ConsultBar, DispositionView, MonitorBar,
  useAcdCall, useAnsweringItself, useJoiningListenIn,
} from "@/softphone/acd-call";
import { AvailabilityMenu, NoCallsLine, useRepAvailability } from "@/softphone/availability";
import { canDialSecondLine, dialBlockedReason, isEngaged } from "@/softphone/call-control";
import { CallContractDialog, SendContractButton } from "@/softphone/call-contract";
import { CallFormLeftNotice, CallFormPane, useCallFormLead } from "@/softphone/call-form";
import { useCallFormSaver } from "@/softphone/call-form-store";
import { intakeCallId, intakePlacement } from "@/lib/intake-steps";
import { PaneStripButtons, showPane, useOfferPanes, usePaneCount, usePanes } from "@/softphone/call-panes";
import { ScriptPane } from "@/softphone/call-script";
import { TranscriptPane } from "@/softphone/call-transcript";
import { TextsPane, useLeadConversation } from "@/softphone/call-texts";
import {
  CallControls, describeParty, EndCallButton, IncomingBanner, KeypadView, ListenInConnecting,
  ParkedRow, RingingMark, stateText, TransferDialog, typingElsewhere, type SubView,
} from "@/softphone/call-views";
import {
  CallRoster, DirectConsultBar, InviteStrip, useCallerHeld, useDirectRoom, useRoomPeople,
} from "@/softphone/direct-call";
import { AddParticipantDialog, ParticipantBar } from "@/softphone/participant";
import { DialerView, useDialerWork } from "@/softphone/dialer-offer";
import { CallBackView } from "@/softphone/call-back";
import { ComingUp } from "@/softphone/coming-up";
import { Dialler } from "@/softphone/dialler";
import { ManualCallView, useManualCallWork, useOwnManualRinging } from "@/softphone/manual-call";
import { BlockCallerAction } from "@/softphone/block-caller";
import { CallerCard, useCallerTitle } from "@/softphone/caller-card";
import { CallerRecordStatus } from "@/softphone/caller-status";
import { IntakePanelMount } from "@/softphone/intake-panel-mount";
import { type CardActions, LeadCardFor, LeadCardPanel, LeadCardView } from "@/softphone/lead-card";
import { MissedCallsList } from "@/softphone/missed-calls";
import { MyCallsButton, useTodayFilters } from "@/softphone/my-calls";
import { PHONE_DOCKED, VoicemailSuggestion } from "@/softphone/voicemail-suggestion";
import { AudioSetupButton, ConnectView, PhoneMenu, StandbyView } from "@/softphone/softphone-dock";
import { useCallDuration, useSoftphone, type Call } from "@/softphone/softphone-context";
import { useMarkStage } from "@/softphone/stage";
import { useCallTranscription } from "@/softphone/transcription-widget";
import { useWorkLead } from "@/softphone/work-lead";

/**
 * The Phone page: where a rep works a call. It takes the whole content area, and most of it is
 * about the work (who this is, the form, the script), not about the telephone.
 *
 * One frame for every phone, with or without a campaign line, so a rep who moves between the two
 * finds every control where it was:
 *  - a strip along the top: the phone's own state, the numbers it answers to, today's counts;
 *  - the band under it, which is the stage: whatever the phone is doing right now. Between calls
 *    that is the dial box and the missed calls, the dialer's lead, or the outcome window; on a
 *    call it is the party in a bar with the campaign the call came through beside them, the
 *    controls in a row, and every bar that asks for an answer (a second call, a briefing, a parked
 *    leg), which therefore never scrolls out of view. On a call the band is as tall as those and
 *    no taller. Between calls, over a work area, it takes at most a little over half the page and
 *    scrolls inside itself, so an offer or twenty outcomes never push the form off the page;
 *  - under the band, while there is a call or a lead to work with, the call's card (the keypad
 *    while it is open, a handoff's note, who this is), and beside it the panes the rep switches
 *    on and off from the phone's rail (`softphone/call-panes.tsx`): the form they fill in
 *    (`softphone/call-form.tsx`) and the one they read, the script (`softphone/call-script.tsx`)
 *    or, in its place, the call's live transcript (`softphone/call-transcript.tsx`). Both side by
 *    side, one of them, or the card by itself: the page is theirs to lay out, and the browser
 *    remembers how they left it. Each pane scrolls by itself;
 *  - along the foot of the page, when the rep asked for them, the people on the call (`CallRoster`
 *    as a tray): a strip of its own under every pane, one tile a person.
 * From a width of 56rem the page is as tall as the content area and the panes sit side by side;
 * below that everything is one column and the content area scrolls, as any page does, and the
 * band is brought back into view when the outcome window opens while the rep is scrolled down in
 * the form. The width is the page's own (a container query), so a folded sidebar and the gallery
 * are both right.
 *
 * The card is as wide as the phone's own panel, which is the width everything in it was drawn
 * for, and it does not repeat the case: the list's columns are not on it, since the form beside
 * it has them. The keypad opens at its top, not in the band: in the band it made the band three
 * times as tall and left the form two fields high. Its chip stays in the band's controls.
 *
 * The form is a sibling of the stage, never inside it: "Dial a number" focuses the first input of
 * the stage and Escape puts the keypad away, and neither may ever land on an answer the rep is
 * typing. It also stays where it is in the tree from the dial through the ring and the call to
 * the outcome window, so the same inputs are on screen the whole way; the lead they belong to is
 * `useWorkLead`'s (`lib/phone-work.ts` holds the rule), and the lead's card is under the band for
 * all of that too, so nothing moves when the lead is brought to the rep. After the call and its
 * window the page keeps that lead until another arrives or the rep closes the form. A lead no
 * call took place with (an offer skipped, a dial nobody answered) is not kept, and a ring shows
 * the ringing stage alone unless it is the call of the lead already on the page.
 *
 * Nothing on it is a second implementation of the panel at the right of every screen. The
 * controls, the views pushed over a call, the consult bar, the roster and the colleague picker
 * are the panel's own components (`softphone/call-views.tsx`, `direct-call.tsx`, `acd-call.tsx`),
 * laid out wider. A rule about what a control may do is decided once, in those modules, and the
 * two surfaces cannot disagree.
 *
 * While this page is on screen the panel never opens itself (`softphone/stage.ts`): everything it
 * would have opened for (the ringing call, the dialer's offer, a number dialed by hand, the
 * outcome window after an ACD call) is here already, at full size, and the panel is the rep's to
 * open if they want it. The day's calls are not on the page any more: they are a button in the
 * phone's rail on every screen (`softphone/my-calls.tsx`), and in the strip where a narrow window
 * has no rail.
 */
export function PhonePage() {
  const { user } = useAuth();
  const phone = useSoftphone();
  if (!user) return null;

  if (user.signed_in_by) {
    return (
      <Page>
        <Card>
          <EmptyState
            icon={PhoneOff}
            title="The phone is off"
            description={`You are signed in as ${user.display_name || user.email}, so their phone stays theirs: no calls ring here, and none go out as them. Exit to use your own.`}
          />
        </Card>
      </Page>
    );
  }
  if (!phone.available) {
    return (
      <Page>
        <Card>
          <EmptyState
            icon={PhoneOff}
            title="No phone assigned"
            description="An admin needs to assign you an endpoint before you can make or take calls. Until then the phone stays hidden."
          />
        </Card>
      </Page>
    );
  }
  // Not in a `Page`: the workspace fills the content area edge to edge, as the board does
  // (`routes/opportunities.tsx`).
  return (
    <PhoneWorkspace
      mine={user.sip_endpoint_numbers}
      recordCalls={user.sip_endpoint_record_calls === true}
    />
  );
}

/**
 * The page's body, given what it needs to know about the account: exported so the state gallery
 * (`routes/dev-phone.tsx`) can render it over a pretend phone with a pretend rep.
 */
export function PhoneWorkspace({
  mine, recordCalls,
}: {
  /** Every identifier the account's phone answers to, from `/auth/me`. */
  mine: readonly string[];
  /** The account records its calls. */
  recordCalls: boolean;
}) {
  const phone = useSoftphone();
  useMarkStage();
  // The form's saves reach this account's query cache through here, once for the page.
  useCallFormSaver();
  const [view, setView] = React.useState<SubView | null>(null);
  const stageRef = React.useRef<HTMLElement>(null);
  // The control that opened a view, so Back can hand the keyboard back to it once the call
  // view is on screen again — an effect, not a timer, because the control does not exist
  // until React has committed the swap.
  const returnFocusTo = React.useRef<SubView | null>(null);

  // A view belongs to the leg it was opened over (`softphone-dock.tsx` keeps the same rule).
  const activeId = phone.activeCallId;
  React.useEffect(() => {
    setView(null);
  }, [activeId]);
  React.useEffect(() => {
    if (view !== null || !returnFocusTo.current) return;
    const tile = returnFocusTo.current;
    returnFocusTo.current = null;
    stageRef.current?.querySelector<HTMLElement>(`[data-tile="${tile}"]`)?.focus();
  }, [view]);
  const pop = () => {
    returnFocusTo.current = view;
    setView(null);
  };
  const push = (next: SubView) => setView(next);

  const active = phone.calls.find((c) => c.id === phone.activeCallId) ?? phone.primary;
  const ringingIn = active !== null && active.direction === "inbound" && active.state === "ringing";
  // Between calls a campaign phone has two things of its own, and the panel does not open over
  // this page to show them: the ACD's outcome window, and the lead the dialer is offering.
  const inWindow = useRepAvailability()?.view.state === "disposition";
  const work = useDialerWork();
  const manual = useManualCallWork();
  const ownRinging = useOwnManualRinging();
  const joining = useJoiningListenIn(active?.id ?? null);
  // A call the phone is picking up by itself (this browser's auto-answer, or the campaign's) is
  // not a call waiting for the rep. With auto-answer on, Answer and Decline came and went on every
  // call before anybody could have pressed them; the page shows the call instead, saying it is
  // connecting, and then that it is connected. A number the rep dialed by hand and a supervisor's
  // listen-in are answered the same way and keep the views they have for that moment.
  const answering = useAnsweringItself(ringingIn ? active.id : null);
  const pickedUp = answering && !(manual.shown && ownRinging === active?.id) && !joining;
  /** The first call ringing, waiting for the rep to answer or decline it. */
  const ringingFirst = ringingIn && !pickedUp;
  /** The call the rep is on, or is being put on by the phone, as opposed to one still ringing. */
  const call = active !== null && !ringingFirst ? active : null;
  const registered = phone.registration === "registered";
  const docked = useMediaQuery(PHONE_DOCKED);

  // The lead whose card, script and form are under the band. While a call rings it is there only
  // for the lead already on the page (the dialer's own call arriving), so any other ring has the
  // page to itself.
  const { lead, live, close, campaignCall } = useWorkLead(active, pickedUp);
  const onCampaignCall = call !== null && campaignCall;
  // The form's answers follow the lead, not the form's pane: a pane put away is not a lead left.
  useCallFormLead(lead);
  // The form and the script are the rep's to have on the page or not (`softphone/call-panes.tsx`).
  // They belong to a campaign's call or lead; any other call has neither, and no switch for them.
  // The form is for the page's lead, kept ones included, since finishing it is what a lead is kept
  // for; the script is for a call or a lead something still names.
  const panes = usePanes();
  const formApplies = lead !== null || onCampaignCall;
  const scriptApplies = onCampaignCall || (lead !== null && live);
  // The intake is the form's equal here: it belongs to a call OR to the page's lead, kept ones
  // included. It used to be offered from inside the call band, which unmounted at hangup and took
  // its rail button with it — exactly when a rep in the disposition window still has questions to
  // finish. Whether there is a flow to walk is the panel's to say (`hasIntake`), since only it can
  // read the lead's run; whether there is anywhere to put it is the page's.
  const [hasIntake, setHasIntake] = React.useState(false);
  // The call the intake is about, kept past the hangup. `useIntakeLead` matches this against the
  // live record and then against the stay still waiting for an outcome (`stayIsAbout`), so it has
  // to stay the call's own id: a lead id would match neither and the panel would blank in the
  // disposition window, which is the one place it must not. Cleared when the phone goes idle, the
  // same moment the hook lets its own held lead go.
  const [lastCallId, setLastCallId] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (call !== null) setLastCallId(call.id);
    else if (phone.calls.length === 0) setLastCallId(null);
  }, [call, phone.calls.length]);
  const onIntakeCall = intakeCallId(call?.id ?? null, lastCallId, phone.calls.length);
  // Where the panel stands, and whether the rep sees it: two answers, because the panel is the
  // only thing that can report `hasIntake` and so must be mounted before the answer is known
  // (`intakePlacement` holds the rule, and the reason it cannot be one boolean).
  const intake = intakePlacement(onIntakeCall, call !== null || lead !== null, hasIntake, panes.intake);
  // Offered on the same terms as the form: there is a call for it to be about and somewhere to
  // put it. It used to wait for `hasIntake` as well — the panel's word that this lead has a flow
  // to walk — which arrives only after the run has been read, so the Intake switch appeared a
  // beat late, or never, while Form and Script were there from the first frame. A rep reading a
  // rail with two of the three on it has no way to know the third is still coming.
  // The lead's texts (ACL-561) belong to the page's lead, on the call and after it, as the form does.
  const textsApply = lead !== null;
  // The call's live transcript (`softphone/call-transcript.tsx`) belongs to the call, any call: a
  // number dialed by hand is transcribed as a campaign's call is. After the call the lines are
  // held until the next call replaces them (`use-transcription.ts`), so a page that still has the
  // lead keeps the pane to read back; with nothing under the band there is nowhere to put it.
  const transcription = useCallTranscription();
  const transcriptHeld =
    transcription.on || transcription.pending || transcription.error !== null ||
    transcription.finals.length > 0 || transcription.partials.length > 0;
  const transcriptApplies = call !== null || (lead !== null && transcriptHeld);
  useOfferPanes({
    form: formApplies, script: scriptApplies, intake: intake.placed, texts: textsApply, transcript: transcriptApplies,
  });
  const leadTexts = useLeadConversation(lead?.id ?? null).row;
  usePaneCount("texts", leadTexts?.unread ?? 0);
  // A lead who texts during the call is shown to the rep: the pane comes up by itself, without
  // being remembered, as the form does for Send contract (`showPane`). What they sent before the
  // call is history and opens nothing (`textArrival`).
  const textsSeen = React.useRef<TextsSeen | null>(null);
  const textsKey = call !== null && lead !== null ? `${call.id}:${lead.id}` : null;
  const lastTextAt = leadTexts?.last_message_at ?? null;
  const lastTextIn = leadTexts?.last_direction === "inbound";
  const textsKnown = leadTexts !== undefined;
  React.useEffect(() => {
    if (textsKey === null) {
      textsSeen.current = null;
      return;
    }
    if (!textsKnown) return;
    const next = textArrival(textsSeen.current, textsKey, lastTextAt ? { at: lastTextAt, inbound: lastTextIn } : null);
    textsSeen.current = next.seen;
    if (next.arrived) showPane("texts");
  }, [textsKey, textsKnown, lastTextAt, lastTextIn]);

  // A transcript that starts is shown: the pane comes up by itself when the rep presses
  // Transcribe (or when the start fails, so the error is read), in the script's place and without
  // being remembered (`showPane`), as the texts do for a text. It used to appear at the foot of
  // the card on the same test. A rep who puts it away keeps it away until the next transcript.
  React.useEffect(() => {
    if (transcriptHeld) showPane("transcript");
  }, [transcriptHeld]);

  // A call opens on the form, whichever of the pair was up before it (ACL-573). Its folders —
  // Contact, Required Intake, Full Intake (ACL-558) — are what a rep asks while the lead is
  // talking, so they are on screen from the first word rather than one press away in the rail.
  // Once per call and not remembered (`showPane`): a rep who brings up the intake's questions
  // instead keeps them until the next call, and the choice stored in this browser is left alone.
  // Only where the form applies, so a call with no lead and no campaign leaves the panes as they are.
  const formOpenedFor = React.useRef<string | null>(null);
  const callId = call?.id ?? null;
  React.useEffect(() => {
    if (callId === null || !formApplies || formOpenedFor.current === callId) return;
    formOpenedFor.current = callId;
    showPane("form");
  }, [callId, formApplies]);

  // Send contract on the call's card (ACL-560, `softphone/call-contract.tsx`): on the call and after it,
  // for the page's lead (`contractOffered`). The dialog is the page's, not the card's, so the end of the
  // call does not close it as the card is drawn again for the outcome window. What it lists to fix is
  // marked on the form, which comes up for it, until the next press says otherwise or another lead
  // arrives; a lead going for a moment while the record catches up keeps it.
  const leadId = lead?.id ?? null;
  const contractShown = lead !== null && contractOffered({ onCall: call !== null, inWindow, live });
  const [sendingFor, setSendingFor] = React.useState<string | null>(null);
  const [asked, setAsked] = React.useState<{ lead: string; ask: ContractAsk } | null>(null);
  React.useEffect(() => {
    if (leadId !== null) setAsked((was) => (was !== null && was.lead !== leadId ? null : was));
  }, [leadId]);
  // Send contract on the caller's own row of the accident (ACL-671), and only on the card of the page's
  // own lead: the contract goes to the lead the form is for. Create new contact (ACL-626) is gone from
  // here: Add a party/case on that panel makes the record of somebody else in the caller's accident (ADR-226).
  const cardActions: CardActions | undefined = contractShown
    ? (card, row) => (card.id === leadId ? <SendContractButton leadId={card.id} row={row} onPress={() => setSendingFor(card.id)} /> : null)
    : undefined;

  // "On this call" is the rep's to open, and stays as they left it in this browser. An error from
  // the room is said only inside the roster, so one shows it whatever they chose, without
  // changing what they chose: while it does, the button is held down and a press writes nothing,
  // or a rep who never opened the roster would find it open on every call from then on.
  const [peopleChosen, setPeopleChosen] = React.useState(readPeopleShown);
  const listed = call !== null && rosterShown(phone.conference, call.id);
  const peopleForced = listed && phone.conferenceError !== null;
  const peopleShown = listed && (peopleChosen || peopleForced);
  const togglePeople = () => {
    if (peopleForced) return;
    setPeopleChosen(!peopleShown);
    writePeopleShown(!peopleShown);
  };

  const dial = () => {
    stageRef.current?.querySelector<HTMLInputElement>('input[inputmode="tel"], input')?.focus();
  };

  let band: React.ReactNode;
  let outcome = false;
  if (phone.registration === "standby") {
    band = <div className="mx-auto max-w-sm p-8"><StandbyView /></div>;
  } else if (!registered) {
    band = <div className="mx-auto max-w-sm p-8"><ConnectView /></div>;
  } else if (!active && inWindow) {
    // Full width: there can be twenty outcomes, so the wider the band the fewer rows to read.
    outcome = true;
    band = <div className="p-5"><DispositionView /></div>;
  } else if (!active && manual.shown) {
    // A number the rep dialed by hand (ADR-047): placing, ringing, or how it failed. The same
    // row a call the dialer places is, a few lines down.
    band = <ManualCallView wide />;
  } else if (!active && (work.offer !== null || work.dialing)) {
    // The lead's own card is under the band while the page has them, so the dial names them and
    // says how far it has got, and leaves the rest to the card.
    band = <DialerView wide facts={lead === null} />;
  } else if (!active) {
    band = <IdleStage docked={docked} />;
  } else if (ringingFirst && manual.shown && ownRinging === active.id) {
    // The ACD ringing this phone with the call the rep placed, which the phone answers itself.
    band = <ManualCallView wide />;
  } else if (ringingFirst && joining) {
    // A supervisor's listen-in, which the phone answers itself: connecting, not Answer / Decline.
    band = <div className="mx-auto max-w-md p-8"><ListenInConnecting call={active} /></div>;
  } else if (ringingFirst) {
    band = <IncomingStage call={active} mine={mine} />;
  } else {
    band = (
      <CallBand
        call={active}
        view={view}
        onPush={push}
        onPop={pop}
        recordCalls={recordCalls}
        people={listed ? { shown: peopleShown, forced: peopleForced, onToggle: togglePeople } : null}
        docked={docked}
        lookupCaller={lead === null}
      />
    );
  }

  // Under the band: always on a call, and between calls while the page has a lead. First the
  // call's card, always; then the form and the script, each only while the rep has it switched
  // on. The form is for the page's lead; a campaign call whose caller has no contact yet gets the
  // pane too, to say so.
  const formShown = formApplies && panes.form;
  const scriptShown = scriptApplies && panes.script;
  const textsShown = textsApply && panes.texts;
  const transcriptShown = transcriptApplies && panes.transcript;
  // The work is on the page for a call and for the page's lead, a kept one as much as a live one.
  // The form's switch in the rail takes the form's sheet off the page and puts it back, and that
  // is all it does. A kept lead's card used to go with its form, because the card alone had no
  // way to be closed; so a rep who put the form away after a call lost the card of the lead they
  // had just spoken to as well. The card has its own Close for that case now (below).
  const workShown = call !== null || lead !== null;
  // The card, and a column for each pane that is up — the intake as much as the form. Counting
  // only the other two put the card and the intake in ONE column whenever the intake was the
  // pane the rep had on, which is the default: the card took the column's free height (`flex-1`)
  // and the questions were pinned to the foot of the page, growing upwards as they were
  // answered. The intake is a sheet of fields a rep works down, exactly as the form is, so it
  // belongs beside the card on the same terms.
  const columns = 1 + Number(formShown) + Number(scriptShown) + Number(intake.shown) + Number(textsShown)
    + Number(transcriptShown);

  // Stacked in one column, a rep typing in the form has scrolled the band off the top. When the
  // outcome window opens there, its question and its timer would be out of sight, so the band is
  // brought back. In the two-pane layout the page does not scroll and this finds nothing to do.
  React.useEffect(() => {
    if (outcome) bringIntoView(stageRef.current);
  }, [outcome]);

  // Escape puts the keypad away, from the band (where its chip is) and from the keypad itself.
  // Transfer and Add participant are dialogs, which close themselves; and a key pressed in a
  // dialog or a menu opened from here reaches these handlers through React's tree, not the
  // page's, and is not theirs to take.
  const escapeKeypad = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === "Escape" && view === "keypad" && e.currentTarget.contains(e.target as Node)) {
      e.stopPropagation();
      pop();
    }
  };

  return (
    <div className="@container/phone h-full">
      <div className="flex flex-col bg-frame @4xl/phone:h-full @4xl/phone:min-h-[34rem]">
        {/* The strip is the frame's top pane: beside the sidebar and the phone's rail, on the
            frame they are on, with the page a sheet under it (`FrameTop`). Where nothing frames
            the page (the state gallery), it is the same strip over the same sheet, drawn here. */}
        <FrameTop>
          <Chrome onCall={call !== null} onDial={dial} mine={mine} docked={docked} />
        </FrameTop>
        {/* The page's own sheet. Its lift is the shell's to draw, around `main` (`app-shell.tsx`),
            which this fills; the shadow here is for where nothing frames the page (the gallery). */}
        <div className="sheet-lift flex min-h-0 flex-1 flex-col rounded-t-2xl bg-surface">
          {phone.error && <Callout tone="danger" className="mx-4 mt-3 shrink-0">{phone.error}</Callout>}
          <NoCallsLine className="mx-4 mt-3 shrink-0" />
          {/* The form pane says this at its own top. With no pane on screen the page says it. */}
          {!formShown && <CallFormLeftNotice className="mx-4 mt-3 shrink-0" />}
          <section
            ref={stageRef}
            aria-label="Your phone"
            className={cn(
              "min-w-0",
              // With nothing under it the band is the page. With the work under it, on a call, it
              // keeps its own height, so the bars of a call never scroll away. Between calls (an
              // offer, a dial, the outcome window with its twenty buttons, the idle stage over a
              // kept lead, a ring for the lead on the page) it gives way instead, so the form
              // stays on the page.
              // It has no line under it. The work sheet under it is set off by its own tone and
              // its lift, and the card beside that sheet by the room between it and the band; a
              // line over the card alone stopped short at the sheet and read as a mistake.
              workShown
                ? "shrink-0"
                : "min-h-0 flex-1 overflow-y-auto scrollbar-subtle",
              workShown && call === null && "@4xl/phone:max-h-[55%] @4xl/phone:overflow-y-auto scrollbar-subtle",
            )}
            onKeyDown={escapeKeypad}
          >
            {band}
          </section>
          {workShown && (
            <div
              className={cn(
                // `m-4`: room enough for the sheet's lift and its rounded corners, and the rest of the
                // width is the form's (ACL-605; it was `m-8`).
                "m-4 flex min-h-0 flex-1 flex-col @4xl/phone:grid @4xl/phone:min-h-56 @4xl/phone:grid-rows-[minmax(0,1fr)]",
                // The card keeps the width it has in the phone's own panel, where everything in it
                // was drawn to fit; the work sheet has the rest.
                columns === 1 ? "@4xl/phone:grid-cols-1" : "@4xl/phone:grid-cols-[20rem_minmax(0,1fr)]",
              )}
            >
              <CardPane alone={columns === 1}>
                {/* Here and not in the band, so the form beside it keeps its height. */}
                {call !== null && view === "keypad" && (
                  <div className="max-w-md" onKeyDown={escapeKeypad}>
                    <KeypadView call={call} onBack={pop} withEndCall={false} />
                  </div>
                )}
                {call !== null ? (
                  <>
                    {/* What a handoff brings with it. A narrow window says it in the band's strip. */}
                    {docked && <AcdHandoffNote callId={call.id} />}
                    {onCampaignCall || lead === null ? (
                      <LeadCardPanel callId={call.id} full frame={false} actions={cardActions} />
                    ) : (
                      // The page has the lead and the rep's record does not name the call (it has
                      // not caught up with a dialer call yet, or has let go of one that is ending):
                      // the same card by the lead's id, so it does not blink out for that moment.
                      <LeadCardFor
                        leadId={lead.id}
                        stay={lead.stay}
                        campaignId={lead.campaignId}
                        full
                        frame={false}
                        actions={cardActions}
                      />
                    )}
                    <CallerRecordStatus callId={call.id} />
                    {/* In place of a lead's card: the contact the caller's number belongs to, drawn
                        as the dialer's lead is, or who this is (ACL-483). */}
                    {lead === null && <CallerCard call={call} mine={mine} />}
                    {/* Under the card, where the rep's eye has already been: over the name it was
                        the first thing on the page. */}
                    <BlockCallerAction call={call} className="self-start" />
                  </>
                ) : (
                  // Between calls the card is the same card by the lead's id: under the dial, in the
                  // outcome window, and for a lead the page only keeps.
                  lead !== null && (
                    <>
                      {/* A lead the page only keeps, with its form put away. The form's header is
                          where a kept lead says the call has ended and where its Close is; without
                          the form the card says both itself, or it would sit under the idle stage
                          with no way to close it. One Close on the page, never two. */}
                      {!live && !formShown && (
                        <div className="flex items-center gap-2">
                          <p className="min-w-0 flex-1 text-xs text-text-muted">The call has ended.</p>
                          <Button variant="ghost" size="icon-sm" aria-label="Close this lead" onClick={close}>
                            <X />
                          </Button>
                        </div>
                      )}
                      <LeadCardFor
                        leadId={lead.id}
                        stay={lead.stay}
                        campaignId={lead.campaignId}
                        full
                        frame={false}
                        actions={cardActions}
                      />
                    </>
                  )
                )}
              </CardPane>
              {(formShown || scriptShown || intake.shown || textsShown || transcriptShown) && (
                <div
                  className={cn(
                    WORK_SHEET,
                    // Two of them share the sheet, or one has all of it. The intake and the form
                    // never co-occur, nor do the script, the texts and the transcript
                    // (`togglePane`), so this is at most a pair: a sheet to fill in and one to read.
                    (formShown || intake.shown) && (scriptShown || textsShown || transcriptShown)
                      ? "@4xl/phone:grid-cols-2"
                      : "@4xl/phone:grid-cols-1",
                  )}
                >
                  {/* Beside the form rather than under the call, so it survives the hangup: the
                      disposition window is when a rep finishes the questions. */}
                  {intake.shown && (
                    <IntakePanelMount
                      callId={onIntakeCall ?? ""}
                      full
                      onHas={setHasIntake}
                      // A pane of the sheet like the other two, lying across its header row and
                      // its body row (`SHEET_PANE`). Without it the questions were a plain block
                      // in a grid of panes: they fell into the header row alone, so they sat in a
                      // band the height of a title, at the foot of the sheet.
                      className={SHEET_PANE}
                    />
                  )}
                  {/* Under the stage's sibling and always in this place, so the answers being typed
                      stay in their inputs from the dial through the ring and the call to the outcome
                      window, and while the script beside it comes and goes. */}
                  {formShown && (
                    <CallFormPane
                      lead={lead}
                      // A caller with no contact has no form to finish later: nothing to close.
                      live={lead === null || live}
                      onClose={close}
                      className={SHEET_PANE}
                      ask={asked !== null && asked.lead === leadId ? asked.ask : null}
                    />
                  )}
                  {/* After the form, next to the rail it is brought up from. */}
                  {scriptShown && (
                    <ScriptPane
                      className={cn(SHEET_PANE, (formShown || intake.shown) && SECOND_PANE)}
                      bodyClassName={formShown || intake.shown ? SECOND_PANE_BODY : undefined}
                    />
                  )}
                  {/* In the script's place, which it takes turns with (ACL-561). */}
                  {textsShown && (
                    <TextsPane
                      leadId={lead.id}
                      className={cn(SHEET_PANE, (formShown || intake.shown) && SECOND_PANE)}
                      bodyClassName={formShown || intake.shown ? SECOND_PANE_BODY : undefined}
                    />
                  )}
                  {/* In the script's place as well, with the sheet's full height to read it in. It
                      was a strip at the foot of the call's card. */}
                  {transcriptShown && (
                    <TranscriptPane
                      className={cn(SHEET_PANE, (formShown || intake.shown) && SECOND_PANE)}
                      bodyClassName={formShown || intake.shown ? SECOND_PANE_BODY : undefined}
                    />
                  )}
                </div>
              )}
            </div>
          )}
          {/* Send contract's dialog, kept while it is open even if the card's button goes for a moment. */}
          {lead !== null && (contractShown || sendingFor === lead.id) && (
            <CallContractDialog
              key={lead.id}
              lead={lead}
              open={sendingFor === lead.id}
              onOpenChange={(open) => setSendingFor(open ? lead.id : null)}
              onAsk={(ask) => {
                setAsked(ask ? { lead: lead.id, ask } : null);
                // The marks are on the form: it comes up for them, in place of the intake.
                if (ask) showPane("form");
              }}
            />
          )}
          {/* The panel again, drawing nothing, for the one job only it can do: saying whether this
              lead has a flow to walk. The page needs that answer *before* it can offer the pane,
              and the copy above is inside the pane it would be offering — so without this one the
              switch could never appear and neither could the questions (`intakePlacement`). It is
              here rather than hidden inside the sheet because the sheet is not drawn at all when
              the intake is the only pane and the rep has it switched off. */}
          {intake.placed && !intake.shown && (
            <div className="hidden" aria-hidden>
              <IntakePanelMount callId={onIntakeCall ?? ""} onHas={setHasIntake} />
            </div>
          )}
          {/* The people on the call, when the rep asked for them: a strip of its own along the foot
              of the page, under every pane, where each of them has the width for a name. It was the
              first block of the card's column, which is 20rem wide. Where the page is one column
              and scrolls, it stays at the foot of what is on screen. */}
          {call !== null && peopleShown && (
            <section
              id="phone-people"
              aria-label="On this call"
              className="sticky bottom-0 z-10 shrink-0 border-t border-border bg-surface px-4 py-2 @4xl/phone:static"
            >
              <CallRoster
                callId={call.id}
                layout="tray"
                // Not while a problem with the room holds it open: the message is drawn in it.
                onClose={peopleForced ? undefined : togglePeople}
              />
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Scrolls the page so this element's top is in view, only when it is above what is visible: the
 * top of the nearest ancestor that scrolls (the content area), or of the window when none does.
 * An element already in view, or below it, is left where it is.
 */
function bringIntoView(el: HTMLElement | null): void {
  if (el === null) return;
  let top = 0;
  for (let up = el.parentElement; up !== null; up = up.parentElement) {
    const { overflowY } = getComputedStyle(up);
    if ((overflowY === "auto" || overflowY === "scroll") && up.scrollHeight > up.clientHeight) {
      top = up.getBoundingClientRect().top;
      break;
    }
  }
  if (el.getBoundingClientRect().top < top) el.scrollIntoView({ block: "start" });
}

/**
 * The work sheet: what the form and the script are on. A second sheet inside the page's own, the
 * way the page is a sheet inside the frame (`components/layout/app-shell.tsx`): a tone of its own
 * (`--color-worksheet`), the same slight lift (`.sheet-lift`), and no line between it and what is
 * around it. The band over it and the call's card beside it stay on the page's white, so what the
 * rep reads about the call and what they work in are two grounds. One sheet for both panes, or
 * for the one that is on; with neither, there is no sheet and the card has the page. Under the
 * card where the page is one column, beside it from the width at which the page is side by side.
 *
 * All four corners are rounded. It used to round only its top left and run off the page's right
 * and foot, which is right for a sheet flush to those edges; the work area is inset from them now
 * (`m-4`), so three square corners would sit exposed against the page's white.
 *
 * Its lift is a step more than the page's (`sheet-lift-2`): the page's is read against the frame's
 * grey, this one against white, where the same shadow is next to nothing.
 *
 * Side by side the sheet is two rows, the panes' headers and their bodies, and each pane lies
 * across both (`SHEET_PANE`). So the two headers are one band of one height, with one line under
 * it, however many rows the form's folders take; and the only rule between the panes is between
 * their bodies (`SECOND_PANE_BODY`), since a line through the band cut one header into two.
 *
 * `overflow-hidden` is what rounds the first pane's header with it.
 */
const WORK_SHEET =
  "sheet-lift-2 flex min-h-0 min-w-0 flex-col overflow-hidden rounded-2xl bg-worksheet @4xl/phone:grid @4xl/phone:grid-rows-[auto_minmax(0,1fr)]";

/** A pane on the work sheet: a header and a body (`softphone/call-form.tsx`, `call-script.tsx`),
 *  one under the other in one column, and on the sheet's own two rows side by side. */
const SHEET_PANE = "min-w-0 @4xl/phone:row-span-2 @4xl/phone:grid @4xl/phone:grid-rows-subgrid";

/** The second of two panes, under the first where the page is one column: ruled off from the
 *  answers above it. Side by side it has no line of its own; its body has (below). */
const SECOND_PANE = "border-t border-border @4xl/phone:border-t-0";

/** The second pane's body, side by side: ruled off from the body beside it, under the band. */
const SECOND_PANE_BODY = "@4xl/phone:border-l @4xl/phone:border-border";

/**
 * The call's card and what goes with it (the keypad while it is open, a handoff's note, who this
 * is): its own scroll. The live transcript was pinned at its foot, in a strip two fifths of the
 * card's height; it is a pane of the work sheet now (`TranscriptPane`), with the sheet's full
 * height to read it in. Beside the form or the script it is as wide as the phone's own panel,
 * which is the width everything in it was drawn for. `alone`, with neither beside it, it has the
 * page and keeps to a width a line can be read at.
 *
 * Where the page is one column (a tablet, a narrow window) the panes are under the card, and the
 * whole card came first: a rep scrolled past the lead's details to reach the questions they were
 * asking. There it is held to part of the screen and scrolls inside itself, so the form starts on
 * the first screen (ACL-605). Side by side, or with nothing under it, it has its full height.
 */
function CardPane({ alone, children }: { alone: boolean; children: React.ReactNode }) {
  return (
    <section
      aria-label="About this call"
      className={cn("flex min-h-0 min-w-0 flex-col", !alone && "max-h-[40dvh] @4xl/phone:max-h-none")}
    >
      {/* `relative`: the containing block of every `sr-only` label in here, which would otherwise
          be placed against the page and stretch it (`app-shell.tsx` says the same of `main`). */}
      <div className={cn("relative flex min-h-0 flex-1 flex-col overflow-y-auto scrollbar-subtle", alone ? "p-4" : "p-3")}>
        <div className={cn("flex flex-1 flex-col gap-3", alone && "max-w-3xl")}>{children}</div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------------------------------ */
/* The strip along the top                                                                     */
/* ------------------------------------------------------------------------------------------ */

/**
 * A campaign rep's taking-calls control first and large (`AvailabilityMenu`, absent for a phone
 * with no lines), the options, the numbers the phone answers to, today's counts, and Dial a
 * number between calls. Whether the phone is connected is not said here: a phone that is not
 * shows Connect in place of the stage, and the call bar says what a call is doing.
 *
 * The day's list is the rail's (`MyCallsRailButton`), and so are the switches for the form and
 * the script (`PaneRailButtons`). A narrow window has no rail, so there the strip carries the same
 * list behind a button and the same two switches. The counts are asked for with the list's own
 * filters (`useTodayFilters`), so the two share what is in the cache.
 */
function Chrome({
  onCall, onDial, mine, docked,
}: {
  onCall: boolean;
  onDial: () => void;
  mine: readonly string[];
  /** The window is wide enough for the phone's rail. */
  docked: boolean;
}) {
  const phone = useSoftphone();
  const number = ownNumber(phone.credentials);
  const today = useTodayFilters();
  const summary = useCallSummary(today, { refetchInterval: 60_000 });
  const { calls, minutes } = daySummary(summary.data);
  const dialBlocked = dialBlockedReason(phone.registration, canDialSecondLine(phone.calls)) !== null;

  return (
    // As tall as the sidebar's head and the rail's first cell, which it sits between.
    <div className="flex min-h-12 shrink-0 flex-wrap items-center gap-3 px-4 py-1.5">
      <AvailabilityMenu large />
      <AudioSetupButton />
      <PhoneMenu />
      {phone.credentials && (
        <span className="flex flex-wrap items-center gap-1.5">
          <Badge tone="outline">
            ext. <span className="font-mono">{phone.credentials.extension}</span>
          </Badge>
          {number && (
            <Badge tone="outline">
              Your number <span className="font-mono">{formatPhoneNumber(number)}</span>
            </Badge>
          )}
        </span>
      )}
      <span className="flex-1" />
      <Stat label="Calls today" value={String(calls)} />
      <Stat label="On the phone" value={`${minutes} min`} />
      {!docked && <PaneStripButtons />}
      {!docked && <MyCallsButton mine={mine} />}
      {!onCall && (
        <Button variant="outline" disabled={dialBlocked} onClick={onDial}>
          <PhoneOutgoing />
          Dial a number
        </Button>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <span className="flex items-baseline gap-1.5 tabular">
      <span className="text-xs text-text-muted">{label}</span>
      <span className="text-sm font-semibold text-text">{value}</span>
    </span>
  );
}

function SectionLabel({ children, className }: { children: React.ReactNode; className?: string }) {
  return <h3 className={cn("mb-2 text-xs font-medium text-text-muted", className)}>{children}</h3>;
}

/* ------------------------------------------------------------------------------------------ */
/* The stage: nothing in progress                                                              */
/* ------------------------------------------------------------------------------------------ */

/**
 * Connected and idle: a box to dial from. That the phone is connected, and the numbers it answers
 * to, are in the strip above; a sentence saying so here took the best of the stage and told the
 * rep nothing.
 *
 * The calls that were missed were the other half of this stage. They are a button in the phone's
 * rail now (`MissedCallsRailButton` in `softphone/missed-calls.tsx`), on every page, so here the
 * Dial box has the stage to itself, set in the middle as the other things a stage says alone are.
 * A narrow window has no rail, so there the same list is still under the Dial box.
 */
function IdleStage({ docked }: { docked: boolean }) {
  const phone = useSoftphone();
  const blocked = dialBlockedReason(phone.registration, canDialSecondLine(phone.calls));
  const dial = (
    <div>
      <SectionLabel>Dial a number</SectionLabel>
      <Dialler
        layout="inline"
        keypadOffered={false}
        disabled={blocked !== null}
        disabledReason={blocked ?? undefined}
      />
      <p className="mt-2 text-xs text-text-muted">
        A number or an extension. Picking a colleague dials at once.
      </p>
    </div>
  );
  return (
    <>
      {/* A setter's caller this closer took (ADR-187), and a dropped call to call back (ADR-085),
          first, while they stand. */}
      <CloserHoldBanner className="px-5 pt-5" />
      <div className="px-5 pt-5 empty:hidden"><CallBackView /></div>
      {/* Today's callbacks, the people of one accident together (ACL-671), under the dial box on
          either layout; nothing is drawn with none. */}
      {docked ? (
        <div className="grid gap-6 p-5">
          <div className="mx-auto w-full max-w-md p-3">{dial}</div>
          <ComingUp />
        </div>
      ) : (
        <div className="grid gap-6 p-5">
          {dial}
          <ComingUp />
          <div>
            <SectionLabel>Missed calls</SectionLabel>
            <MissedCallsList rows={5} />
          </div>
        </div>
      )}
    </>
  );
}

/* ------------------------------------------------------------------------------------------ */
/* The stage: a call ringing                                                                   */
/* ------------------------------------------------------------------------------------------ */

/**
 * The first call ringing: who, why (a campaign, a colleague bringing this phone into their
 * call, a lead's card), what this number and this phone have to do with each other, and two
 * choices. Answer takes focus on arrival unless the rep is typing somewhere, as on the panel.
 *
 * A call the ACD did not bring, from a number that is one of our contacts, rings with that
 * contact's card as the ACD's calls ring with theirs, and by their name (ACL-483).
 */
function IncomingStage({ call, mine }: { call: Call; mine: readonly string[] }) {
  const phone = useSoftphone();
  const party = describeParty(call, phone.conference);
  const { internalTransfer } = party;
  const { lookup, title, subtitle } = useCallerTitle(call, party);
  const [takeFocus] = React.useState(() => !typingElsewhere());
  const others = phone.calls.filter(
    (c) => c.id !== call.id && c.direction === "inbound" && c.state === "ringing",
  );
  const { room } = useDirectRoom(call.id, { ringing: true });
  const invited = inviteText(room) !== null;

  return (
    <div className="mx-auto flex w-full max-w-xl flex-col items-center gap-4 px-6 py-10 text-center">
      <RingingMark internalTransfer={internalTransfer} />
      <div className="w-full min-w-0">
        <p className="truncate text-xl font-semibold text-text">{title}</p>
        <p className="truncate text-xs text-text-muted">Incoming call{subtitle && ` · ${subtitle}`}</p>
      </div>
      <AcdCallStrip callId={call.id} />
      <InviteStrip callId={call.id} />
      <LeadCardPanel callId={call.id} />
      {lookup.card && <LeadCardView card={lookup.card} campaignId={lookup.campaignId} />}
      <CallerRecordStatus callId={call.id} />
      {/* The number stays on screen until the contacts have answered, then gives way to the card:
          taking it away while the search ran left nothing where the caller had been. */}
      {!invited && !lookup.card && <ContactStrip remote={call.remote} mine={mine} />}
      <div className="grid w-full grid-cols-2 gap-2">
        <Button
          variant="outline"
          className="h-11 text-sm text-danger-11"
          onClick={() => void phone.reject(call.id)}
        >
          <X />
          Decline
        </Button>
        <Button
          variant="default"
          className="h-11 bg-success-9 text-sm hover:brightness-110"
          onClick={() => void phone.answer(call.id)}
          autoFocus={takeFocus}
        >
          <Phone />
          Answer
        </Button>
      </div>
      {/* Blocking a caller who is ringing declines them as well. */}
      <BlockCallerAction call={call} decline />
      {others.length > 0 && (
        <div className="flex w-full flex-col gap-2 text-left">
          {others.map((c) => (
            <IncomingBanner key={c.id} call={c} decision={false} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Who this number is to this phone: a colleague, or an outside number, and the calls before. */
function ContactStrip({ remote, mine }: { remote: string; mine: readonly string[] }) {
  const colleagues = useColleagues();
  const colleague = colleagueFor(remote, colleagues.data);
  const dialable = isDialable(remote);
  const history = useCalls({ mine: true, exclude_leg: OWN_NON_CALL_LEGS, q: remote }, 0, {
    enabled: dialable && colleague === null,
  });
  const last = history.data?.items[0];
  const before = history.data?.total ?? 0;
  if (!colleague && !dialable) return null;
  return (
    <div className="w-full rounded-lg border border-border px-3 py-2 text-left text-xs">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-text">
          {colleague ? (colleague.name ?? `Extension ${colleague.extension}`) : formatPhoneNumber(remote)}
        </span>
        {colleague ? (
          <Badge tone="outline" size="sm">Colleague · ext. {colleague.extension}</Badge>
        ) : (
          <Badge tone="neutral" size="sm">Outside number</Badge>
        )}
      </div>
      {!colleague && history.data && (
        <p className="mt-0.5 text-text-muted">
          {before === 0
            ? "First call with you."
            : `${before} ${before === 1 ? "call" : "calls"} with you${last ? ` · last one ${relativeTime(last.start_time)}, ${legText(last, mine).toLowerCase()}` : ""}`}
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------------------------ */
/* The stage: the call the rep is on                                                          */
/* ------------------------------------------------------------------------------------------ */

/**
 * The band on a call, top to bottom: the party and the campaign the call came through, how long,
 * whether it is recorded and what state it is in, with the button that shows who is on the call;
 * the six controls and End call in a row; then everything that asks for an answer while it lasts:
 * a voicemail suggestion where the window has no rail, a second call ringing, a briefing in
 * progress, a parked leg. Each of those bars is mounted for the whole call, shown or not, because
 * some of them do work while they wait. The keypad and who this is are under the band, in the
 * call's card, and the people on the call along the foot of the page, so the band never grows
 * with them. Transfer is a dialog over all of it.
 *
 * The campaign is a chip in the party's bar (`AcdCallChip`), and a handoff's note is beside the
 * card: a box across the page to say a campaign's name and "Inbound call" was a bar's worth of
 * page for one line. A narrow window's bar has no room for the chip, so there the same facts are
 * the strip the phone's own panel draws, among the bars.
 */
function CallBand({
  call, view, onPush, onPop, recordCalls, people, docked, lookupCaller,
}: {
  call: Call;
  /** The view pushed over the call. The keypad is drawn by the page, beside the call's card. */
  view: SubView | null;
  onPush: (view: SubView) => void;
  onPop: () => void;
  recordCalls: boolean;
  /** "On this call", when this call has people to list: whether they are shown, and the toggle. */
  people: PeopleToggle | null;
  /** The window is wide enough for the phone's rail, and so for the campaign beside the party. */
  docked: boolean;
  /** The page has no lead for this call, so the bar may name the caller from the contacts. */
  lookupCaller: boolean;
}) {
  const phone = useSoftphone();
  const ringingIn = phone.calls.filter(
    (c) => c.id !== call.id && c.direction === "inbound" && c.state === "ringing",
  );
  const parked = phone.calls.filter((c) => c.id !== call.id && !ringingIn.includes(c));

  return (
    <>
      <CallBar
        call={call}
        recordCalls={recordCalls}
        people={people}
        campaign={docked}
        lookupCaller={lookupCaller}
      />
      {/* On the sheet's own surface, as the bar above it is. It was a sunken well, which is the
          frame's tone: the sidebar's and the rail's colour, running across the middle of the page. */}
      <div className="flex flex-wrap items-center gap-1.5 px-4 py-2">
        <CallControls call={call} onPush={onPush} layout="row" />
        <AskCloserButton />
        <span className="flex-1" />
        <EndCallButton call={call} className="h-9 w-auto px-4" />
      </div>
      <div className="flex flex-col gap-3 border-t border-border px-4 py-3 empty:hidden">
        {/* Only where the window has no rail to carry it; it draws nothing otherwise. */}
        <VoicemailSuggestion />
        {ringingIn.map((c) => (
          <IncomingBanner key={c.id} call={c} decision={c.id === phone.waiting?.id} />
        ))}
        {!docked && <AcdCallStrip callId={call.id} />}
        <ConsultBar />
        <AskCloserStrip />
        <DirectConsultBar callId={call.id} />
        <ParticipantBar callId={call.id} />
        <MonitorBar callId={call.id} />
        {parked.length > 0 && (
          <ul className="flex flex-col gap-1.5" aria-label="Other calls">
            {parked.map((c) => (
              <ParkedRow key={c.id} call={c} />
            ))}
          </ul>
        )}
      </div>
      {/* Over the stage, not in place of it: the call this transfer is about stays behind it. */}
      <TransferDialog open={view === "transfer"} onClose={onPop} />
      <AddParticipantDialog open={view === "add"} callId={call.id} onClose={onPop} />
    </>
  );
}

/** The page's own switch for the people on the call. A view of the page, not something the phone
 *  does, so it is not one of the call's controls (`useCallControls`). */
interface PeopleToggle {
  shown: boolean;
  /** A problem with the call's room is holding the roster open, whatever the rep chose. */
  forced: boolean;
  onToggle: () => void;
}

function CallBar({
  call, recordCalls, people, campaign, lookupCaller,
}: {
  call: Call;
  recordCalls: boolean;
  people: PeopleToggle | null;
  /** Whether the campaign the call came through is said here, beside the party. */
  campaign: boolean;
  /** Whether the caller may be named from the contacts (`useCallerTitle`). */
  lookupCaller: boolean;
}) {
  const phone = useSoftphone();
  const seconds = useCallDuration(call.answeredAt);
  // On a call in a room, how many are on it as the server counts them: said once, on the "On this
  // call" button. Under the party's name it was said again, beside their number.
  const count = useRoomPeople(call.id);
  const party = describeParty(call, phone.conference, count);
  const { isRoom, internalTransfer } = party;
  // A contact who called the rep's own number is named as the dialer's lead is, over their number
  // (ACL-483). The same search as the card under the band, so it is asked once.
  const { title, subtitle } = useCallerTitle(call, party, lookupCaller);
  const transferring = phone.handingOver.includes(call.id);
  const callerHeld = useCallerHeld(call.id);
  const held = call.state === "held" || callerHeld;
  const engaged = isEngaged(call.state);
  // The phone mark says the call's type at a glance (ACL-508): solid green for an inbound call,
  // solid red for a dialer call, beside the chip that says it in words. On hold it says that
  // instead; a room or a handoff keeps the accent.
  const acd = useAcdCall();
  const type = !held && !isRoom && acd?.callId === call.id ? knownCallType(acd.kind) : null;
  const typed = type === "inbound" || type === "dialer" ? type : null;
  // Ringing in and being picked up by the phone itself: "Connecting", not "Incoming".
  const answering = useAnsweringItself(
    call.direction === "inbound" && call.state === "ringing" ? call.id : null,
  );
  return (
    <div className="flex items-center gap-3 border-b border-border px-4 py-2.5">
      <span
        className={cn(
          "flex size-10 shrink-0 items-center justify-center rounded-full",
          held
            ? "bg-warning-2 text-warning-11"
            : typed === "inbound"
              ? "bg-success-9 text-white"
              : typed === "dialer"
                ? "bg-danger-9 text-white"
                : "bg-accent-3 text-accent-11",
        )}
        data-call-type={typed ?? undefined}
      >
        {isRoom ? (
          <Users className="size-[18px]" aria-hidden />
        ) : held ? (
          <Pause className="size-[18px]" aria-hidden />
        ) : internalTransfer ? (
          <Repeat className="size-[18px]" aria-hidden />
        ) : (
          <Phone className="size-[18px]" aria-hidden />
        )}
      </span>
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div className="min-w-0">
          <p className="truncate text-base font-semibold text-text">{title}</p>
          {subtitle && <p className="truncate text-xs text-text-muted">{subtitle}</p>}
        </div>
        {/* Beside the party it is about. Short of room, it is the chip that gives way. */}
        {campaign && <AcdCallChip callId={call.id} className="shrink-[4]" />}
      </div>
      <div className="flex shrink-0 items-center gap-3">
        {/* Hidden until the rep asks: most of a call is two people, and the roster is a strip
            off the foot of the page. The count turns to the accent tone when somebody joined a
            roster they are not looking at; it never opens by itself. */}
        {people && (
          <Button
            variant="ghost"
            size="sm"
            aria-expanded={people.shown}
            aria-controls="phone-people"
            aria-label={people.shown ? "Hide who is on this call" : "Show who is on this call"}
            // Held down while a problem with the room keeps the roster open: the page says why,
            // and the press changes nothing (the page's toggle refuses it).
            aria-pressed={people.forced ? true : undefined}
            title={people.forced ? "Shown while there is a problem with the call" : undefined}
            className={cn(people.forced && "bg-surface-hover text-text")}
            onClick={people.onToggle}
          >
            <Users />
            On this call
            {count !== null && (
              <Badge size="sm" tone={count >= 3 && !people.shown ? "accent" : "neutral"}>
                {count}
              </Badge>
            )}
          </Button>
        )}
        {/* The account's toggle, not the switch's verdict — and a sound test is never recorded. */}
        {recordCalls && engaged && !phone.soundTests.includes(call.id) && (
          <span
            className="inline-flex items-center gap-1.5 text-xs font-medium text-danger-11"
            title="Your account records its calls"
          >
            <span className="size-2 rounded-full bg-danger-9" aria-hidden />
            Recording
          </span>
        )}
        {engaged && (
          <span className="font-mono text-sm tabular text-text" aria-hidden>
            {formatDuration(seconds)}
          </span>
        )}
        <span className={cn("text-xs", held ? "text-warning-11" : "text-text-muted")}>
          {(transferring || answering) && <Spinner className="mr-1 inline size-3 align-[-2px]" />}
          {stateText(call, transferring, callerHeld, answering)}
        </span>
      </div>
    </div>
  );
}
